import {
  AfterViewInit,
  Component,
  OnInit,
  TemplateRef,
  ViewChild,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { AssetInstance } from '../../core/models/asset-instance.model';
import { CheckPoint } from '../../core/models/checkPoint.model';
import { Form } from '../../core/models/form.model';
import { LabelLayout } from '../../core/models/label-layout.model';
import { SheetFormat } from '../../core/models/sheet-format.model';
import { AssetInstanceService } from '../../core/services/asset-instance.service';
import { BarcodeMappingService } from '../../core/services/barcode-mapping.service';
import { BreadcrumbService } from '../../core/services/breadcrumb.service';
import { CheckPointService } from '../../core/services/checkPoint.service';
import { FormService } from '../../core/services/form.service';
import { LabelLayoutService } from '../../core/services/label-layout.service';
import { LabelPdfService } from '../../core/services/label-pdf.service';
import { LoggerService } from '../../core/services/logger.service';
import { ModalConfig, ModalService } from '../../core/services/modal.service';
import {
  SheetFormatService,
  SheetFormatUpsertValues,
} from '../../core/services/sheet-format.service';
import { ToastService } from '../../core/services/toast.service';
import { TranslationService } from '../../core/services/translation.service';
import { UserStorageService } from '../../core/services/user-storage.service';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { CardContentComponent } from '../../shared/components/card/card-content.component';
import { CardHeaderComponent } from '../../shared/components/card/card-header.component';
import { CardTitleComponent } from '../../shared/components/card/card-title.component';
import { CardComponent } from '../../shared/components/card/card.component';
import { CheckpointFormHeaderComponent } from '../../shared/components/checkpoint-form-header/checkpoint-form-header.component';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { SpinnerComponent } from '../../shared/components/spinner/spinner.component';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';
import {
  LabelPreviewComponent,
  LabelPreviewPatch,
} from './components/label-preview/label-preview.component';
import { SheetFormatDeleteConfirmModalComponent } from './components/sheet-format-delete-confirm-modal/sheet-format-delete-confirm-modal.component';
import {
  SheetFormatEditorModalComponent,
  SheetFormatEditorModel,
  SheetFormatEditorUnit,
} from './components/sheet-format-editor-modal/sheet-format-editor-modal.component';
import { SheetGridComponent } from './components/sheet-grid/sheet-grid.component';

/** Per-format layout settings persisted in userStorage */
interface LayoutSettings {
  codeType?: 'qr' | 'barcode';
  fontFamily?: string;
  showCode?: boolean;
  showCheckpointName?: boolean;
  showFormName?: boolean;
  showSerialNumber?: boolean;
  checkpointNameFontSize?: number;
  formNameFontSize?: number;
  serialNumberFontSize?: number;
  qrX?: number;
  qrY?: number;
  qrWidth?: number;
  qrHeight?: number;
  checkpointNameX?: number;
  checkpointNameY?: number;
  checkpointNameWidth?: number;
  checkpointNameHeight?: number;
  formNameX?: number;
  formNameY?: number;
  formNameWidth?: number;
  formNameHeight?: number;
  serialNumberX?: number;
  serialNumberY?: number;
  serialNumberWidth?: number;
  serialNumberHeight?: number;
}

/** Unified model driving all print-config Formly form sections */
interface PrintConfig {
  layoutId: string | null;
  sheetFormatId: string | null;
  codeType: 'qr' | 'barcode';
  fontFamily: string;
  showCode: boolean;
  showCheckpointName: boolean;
  showFormName: boolean;
  showSerialNumber: boolean;
  checkpointNameFontSize: number;
  formNameFontSize: number;
  serialNumberFontSize: number;
  /** QR code X position in mm from label left edge */
  qrX: number;
  /** QR code Y position in mm from label top edge */
  qrY: number;
  /** QR code width in mm */
  qrWidth: number;
  /** QR code height in mm */
  qrHeight: number;
  /** Checkpoint name text X position in mm */
  checkpointNameX: number;
  /** Checkpoint name text Y position in mm */
  checkpointNameY: number;
  /** Checkpoint name text width in mm */
  checkpointNameWidth: number;
  /** Checkpoint name text height in mm */
  checkpointNameHeight: number;
  /** Form name text X position in mm */
  formNameX: number;
  /** Form name text Y position in mm */
  formNameY: number;
  /** Form name text width in mm */
  formNameWidth: number;
  /** Form name text height in mm */
  formNameHeight: number;
  /** Serial number text X position in mm */
  serialNumberX: number;
  /** Serial number text Y position in mm */
  serialNumberY: number;
  /** Serial number text width in mm */
  serialNumberWidth: number;
  /** Serial number text height in mm */
  serialNumberHeight: number;
}

/**
 * Label Printing page — ad-hoc on-demand label printing for a checkpoint-form.
 *
 * Flow:
 * 1. Choose label layout (QR left/right) — card-select Formly type
 * 2. Choose sheet format (from configured sheet definitions) — native-select Formly type
 * 3. Configure text style (font family, font sizes) — native-select + slider Formly types
 * 4. Click label positions on the interactive grid to select/deselect
 * 5. Print → generates PDF via jsPDF and opens browser print dialog
 *
 * The three configurable sections are each driven by a separate <formly-form> that
 * shares a single PrintConfig model object. Field configs are built after data loads
 * so that the layout card TemplateRef is available when constructing the card-select field.
 */
@Component({
  selector: 'app-label-printing',
  imports: [
    ReactiveFormsModule,
    FormlyModule,
    ButtonComponent,
    CardComponent,
    CardHeaderComponent,
    CardTitleComponent,
    CardContentComponent,
    CheckpointFormHeaderComponent,
    IconComponent,
    SpinnerComponent,
    TranslatePipe,
    SheetGridComponent,
    LabelPreviewComponent,
  ],
  templateUrl: './label-printing.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './label-printing.component.css',
})
export class LabelPrintingComponent implements OnInit, AfterViewInit {
  private static readonly MM_PER_INCH = 25.4;
  private static readonly SHEET_EDITOR_UNIT_STORAGE_KEY = 'label-printing:sheet-editor-unit';
  private static readonly SELECTED_INSTANCE_STORAGE_KEY_PREFIX = 'label-printing:selected-instance';

  @ViewChild('labelPreviewTemplate')
  private labelPreviewTemplateRef!: TemplateRef<any>;
  @ViewChild('sheetActionsTemplate')
  private sheetActionsTemplateRef!: TemplateRef<any>;
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private breadcrumbService = inject(BreadcrumbService);
  private checkPointService = inject(CheckPointService);
  private assetInstanceService = inject(AssetInstanceService);
  private barcodeMappingService = inject(BarcodeMappingService);
  private formService = inject(FormService);
  private sheetFormatService = inject(SheetFormatService);
  private labelLayoutService = inject(LabelLayoutService);
  private labelPdfService = inject(LabelPdfService);
  private modalService = inject(ModalService);
  private toast = inject(ToastService);
  private logger = inject(LoggerService).getLogger('label-printing');
  private translate = inject(TranslationService);
  private userStorage = inject(UserStorageService);

  // Page state
  loading = signal(true);
  error = signal<string | null>(null);
  generating = signal(false);

  // Data
  checkPoint = signal<CheckPoint | null>(null);
  form = signal<Form | null>(null);
  sheetFormats = signal<SheetFormat[]>([]);
  labelLayouts = signal<LabelLayout[]>([]);

  // Instance data (for instanced checkpoints)
  instances = signal<AssetInstance[]>([]);
  hasInstances = signal(false);
  /** Map of sheet position → assigned instance */
  instanceAssignments = signal<Map<number, AssetInstance>>(new Map());
  /** Currently selected instance (pending placement on grid) */
  selectedInstance = signal<AssetInstance | null>(null);

  // Print configuration — single unified model shared across the form sections
  printConfigModel: PrintConfig = {
    layoutId: null,
    sheetFormatId: null,
    codeType: 'qr',
    fontFamily: 'helvetica',
    showCode: true,
    showCheckpointName: true,
    showFormName: true,
    showSerialNumber: true,
    checkpointNameFontSize: 10,
    formNameFontSize: 8,
    serialNumberFontSize: 8,
    qrX: 0,
    qrY: 0,
    qrWidth: 0,
    qrHeight: 0,
    checkpointNameX: 0,
    checkpointNameY: 0,
    checkpointNameWidth: 0,
    checkpointNameHeight: 0,
    formNameX: 0,
    formNameY: 0,
    formNameWidth: 0,
    formNameHeight: 0,
    serialNumberX: 0,
    serialNumberY: 0,
    serialNumberWidth: 0,
    serialNumberHeight: 0,
  };

  // Position selection and debug state
  labeledPositions = signal<Set<number>>(new Set());
  usedPositions = signal<Set<number>>(new Set());
  debug = signal(false);

  // Single Formly form + fields driving the entire config panel
  mainForm = new FormGroup({});
  mainFields: FormlyFieldConfig[] = [];

  readonly fontFamilyOptions = [
    { value: 'helvetica', label: 'Helvetica / Arial' },
    { value: 'times', label: 'Times New Roman' },
    { value: 'courier', label: 'Courier New' },
  ];

  // Computed lookups from model IDs
  get selectedLayout(): LabelLayout | null {
    return this.labelLayouts().find((l) => l.id === this.printConfigModel.layoutId) ?? null;
  }

  get selectedSheetFormat(): SheetFormat | null {
    return this.sheetFormats().find((f) => f.id === this.printConfigModel.sheetFormatId) ?? null;
  }

  get qrUrl(): string {
    const cp = this.checkPoint();
    const f = this.form();
    if (!cp || !f) return '';
    return `${window.location.origin}/record/${cp.id}/${f.id}`;
  }

  get totalLabels(): number {
    const fmt = this.selectedSheetFormat;
    return fmt ? fmt.columns * fmt.rows : 0;
  }

  get selectedCount(): number {
    return this.labeledPositions().size;
  }

  get canPrint(): boolean {
    return !!this.selectedLayout && !!this.selectedSheetFormat && this.selectedCount > 0;
  }

  get assignedCount(): number {
    return this.instanceAssignments().size;
  }

  /** Instances available for assignment (can be reused across multiple cells). */
  get assignableInstances(): AssetInstance[] {
    return this.instances().filter((i) => !i.is_deleted);
  }

  /** Map of position → serial number string for grid cell previews */
  get positionSerials(): Map<number, string> {
    const map = new Map<number, string>();
    for (const [pos, inst] of this.instanceAssignments()) {
      map.set(pos, '#' + inst.serial_number);
    }
    return map;
  }

  get canEditSelectedSheetFormat(): boolean {
    const format = this.selectedSheetFormat;
    return !!format && format.is_system === 0;
  }

  get canDeleteSelectedSheetFormat(): boolean {
    const format = this.selectedSheetFormat;
    return !!format && format.is_system === 0;
  }

  /** Set to true once ngAfterViewInit has run and @ViewChild refs are available. */
  private viewInitialized = false;

  /**
   * Shadow copy of the last model state that was fully applied and persisted.
   * Formly mutates printConfigModel in-place before firing (modelChange), so
   * comparing partial against printConfigModel directly would always see equal
   * values. We diff against this shadow instead.
   */
  private _appliedModel: PrintConfig = { ...this.printConfigModel };

  ngOnInit(): void {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.loadData();
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    // If loadData() finished before this hook (data was already cached), build fields now.
    if (!this.loading()) {
      this.buildFields();
    }
  }

  async loadData(): Promise<void> {
    this.loading.set(true);
    this.error.set(null);

    try {
      const checkPointId = this.route.snapshot.paramMap.get('checkpointId');
      const formId = this.route.snapshot.paramMap.get('formId');

      if (!checkPointId || !formId) {
        this.error.set(this.translate.translate('labelPrinting.error.missingParams'));
        this.loading.set(false);
        return;
      }

      // Load checkpoint, form, formats, and layouts in parallel
      const [checkPoint, form, formats, layouts] = await Promise.all([
        this.checkPointService.getById(checkPointId),
        this.formService.getById(formId),
        this.sheetFormatService.getAll(),
        this.labelLayoutService.getAll(),
      ]);

      if (!checkPoint) {
        this.error.set(this.translate.translate('labelPrinting.error.checkpointNotFound'));
        this.loading.set(false);
        return;
      }

      if (!form) {
        this.error.set(this.translate.translate('labelPrinting.error.formNotFound'));
        this.loading.set(false);
        return;
      }

      this.checkPoint.set(checkPoint);
      this.form.set(form);
      this.sheetFormats.set(formats);
      this.labelLayouts.set(layouts);

      // Load instances for this checkpoint
      const allInstances = await this.assetInstanceService.getByCheckpointId(checkPointId);
      this.instances.set(allInstances);
      this.hasInstances.set(allInstances.length > 0);

      // Build initial model, restoring last user selections where possible
      let layoutId: string | null = null;
      let selectedLayoutObj: LabelLayout | null = null;
      let sheetFormatId: string | null = null;

      if (layouts.length > 0) {
        const savedLayoutId = this.userStorage.getItem('label-printing:layout-id');
        selectedLayoutObj = savedLayoutId
          ? (layouts.find((l) => l.id === savedLayoutId) ?? layouts[0])
          : layouts[0];
        layoutId = selectedLayoutObj.id;
      }

      let settings: Required<LayoutSettings> = {
        codeType: 'qr',
        fontFamily: 'helvetica',
        showCode: true,
        showCheckpointName: true,
        showFormName: true,
        showSerialNumber: true,
        checkpointNameFontSize: 10,
        formNameFontSize: 8,
        serialNumberFontSize: 8,
        qrX: 0,
        qrY: 0,
        qrWidth: 0,
        qrHeight: 0,
        checkpointNameX: 0,
        checkpointNameY: 0,
        checkpointNameWidth: 0,
        checkpointNameHeight: 0,
        formNameX: 0,
        formNameY: 0,
        formNameWidth: 0,
        formNameHeight: 0,
        serialNumberX: 0,
        serialNumberY: 0,
        serialNumberWidth: 0,
        serialNumberHeight: 0,
      };
      if (formats.length > 0) {
        const savedFormatId = this.userStorage.getItem('label-printing:format-id');
        const restored = savedFormatId
          ? (formats.find((f) => f.id === savedFormatId) ?? formats[0])
          : formats[0];
        sheetFormatId = restored.id;
        if (selectedLayoutObj) {
          settings = this.loadLayoutSettings(selectedLayoutObj, restored);
        }
      }

      this.printConfigModel = {
        layoutId,
        sheetFormatId,
        codeType: settings.codeType,
        fontFamily: settings.fontFamily,
        showCode: settings.showCode,
        showCheckpointName: settings.showCheckpointName,
        showFormName: settings.showFormName,
        showSerialNumber: settings.showSerialNumber,
        checkpointNameFontSize: settings.checkpointNameFontSize,
        formNameFontSize: settings.formNameFontSize,
        serialNumberFontSize: settings.serialNumberFontSize,
        qrX: settings.qrX,
        qrY: settings.qrY,
        qrWidth: settings.qrWidth,
        qrHeight: settings.qrHeight,
        checkpointNameX: settings.checkpointNameX,
        checkpointNameY: settings.checkpointNameY,
        checkpointNameWidth: settings.checkpointNameWidth,
        checkpointNameHeight: settings.checkpointNameHeight,
        formNameX: settings.formNameX,
        formNameY: settings.formNameY,
        formNameWidth: settings.formNameWidth,
        formNameHeight: settings.formNameHeight,
        serialNumberX: settings.serialNumberX,
        serialNumberY: settings.serialNumberY,
        serialNumberWidth: settings.serialNumberWidth,
        serialNumberHeight: settings.serialNumberHeight,
      };

      // Set breadcrumb
      this.breadcrumbService.setDynamicLabels([checkPoint.name, `${form.name} Labels`]);

      // Restore cell positions for the resolved format
      if (sheetFormatId) {
        const fmt = formats.find((f) => f.id === sheetFormatId);
        if (fmt) {
          const totalCells = fmt.columns * fmt.rows;
          const restored = this.loadCellState(sheetFormatId, totalCells);
          this.labeledPositions.set(restored.labeled);
          this.usedPositions.set(restored.used);
        }
        // Restore instance assignments (if instanced checkpoint)
        if (allInstances.length > 0 && checkPointId) {
          this.loadInstanceAssignments(checkPointId, sheetFormatId, allInstances);
          this.ensureSelectedInstance();
        }
      }

      this.loading.set(false);
      // Sync shadow copy so onMainModelChange diffs correctly from this baseline.
      this._appliedModel = { ...this.printConfigModel };

      // Only build fields if the view is already initialized (TemplateRef is available).
      // If not, ngAfterViewInit will call buildFields() once it runs.
      if (this.viewInitialized) {
        this.buildFields();
      }
    } catch (err) {
      this.logger.error('Failed to load label printing data:', err);
      this.error.set(this.translate.translate('labelPrinting.error.loadFailed'));
      this.loading.set(false);
    }
  }

  // -------------------------------------------------------------------
  // Formly field builders
  // -------------------------------------------------------------------

  private buildFields(): void {
    this.mainFields = this.buildMainFields();
  }

  private section(
    label: string,
    fieldGroupClassName: string,
    fieldGroup: FormlyFieldConfig[],
  ): FormlyFieldConfig {
    return {
      className: 'section-field',
      wrappers: ['field-group-section'],
      props: { label, headingLevel: 'h3' },
      fieldGroupClassName,
      fieldGroup,
    };
  }

  private buildMainFields(): FormlyFieldConfig[] {
    const t = (key: string) => this.translate.translate(key);
    return [
      {
        fieldGroupClassName: 'flex-responsive col-3',
        fieldGroup: [
          // ── Sheet Format ──────────────────────────────────────────────────
          this.section(t('labelPrinting.step.chooseSheet'), '', [
            {
              key: 'sheetFormatId',
              type: 'native-select',
              props: {
                label: t('labelPrinting.step.chooseSheet'),
                options: this.sheetFormats().map((f) => ({
                  value: f.id,
                  label: `${f.name} (${f.columns}\u00d7${f.rows} = ${f.columns * f.rows} ${t('labelPrinting.labels')})`,
                })),
              },
            },
            {
              type: 'template-content',
              props: { template: this.sheetActionsTemplateRef },
            },
          ]),
          // ── Code Type ─────────────────────────────────────────────────────
          this.section(t('labelPrinting.step.codeType'), '', [
            {
              key: 'codeType',
              type: 'native-select',
              props: {
                label: t('labelPrinting.step.codeType'),
                options: [
                  { value: 'qr', label: t('labelPrinting.codeType.qr') },
                  { value: 'barcode', label: t('labelPrinting.codeType.barcode') },
                ],
              },
            },
            {
              key: 'showCode',
              type: 'checkbox',
              props: { label: t('labelPrinting.fontSize.showCode') },
            },
          ]),
          // ── Text Style ────────────────────────────────────────────────────
          this.section(t('labelPrinting.step.fontSize'), '', [
            {
              key: 'fontFamily',
              type: 'native-select',
              props: {
                label: t('labelPrinting.fontSize.fontFamily'),
                options: this.fontFamilyOptions,
              },
            },
          ]),
          // ── Checkpoint Name ───────────────────────────────────────────────
          this.section(t('labelPrinting.textLabel.checkpointName'), '', [
            {
              key: 'showCheckpointName',
              type: 'checkbox',
              props: { label: t('labelPrinting.fontSize.showCheckpointName') },
            },
          ]),
          // ── Form Name ─────────────────────────────────────────────────────
          this.section(t('labelPrinting.textLabel.formName'), '', [
            {
              key: 'showFormName',
              type: 'checkbox',
              props: { label: t('labelPrinting.fontSize.showFormName') },
            },
          ]),
          // ── Serial Number (only for instanced checkpoints) ────────────────
          ...(this.hasInstances()
            ? [
                this.section(t('labelPrinting.textLabel.serialNumber'), '', [
                  {
                    key: 'showSerialNumber',
                    type: 'checkbox',
                    props: { label: t('labelPrinting.fontSize.showSerialNumber') },
                  },
                ]),
              ]
            : []),
          // ── Label Preview ─────────────────────────────────────────────────
          this.section(t('labelPrinting.step.preview'), '', [
            {
              type: 'template-content',
              props: { template: this.labelPreviewTemplateRef },
            },
          ]),
        ],
      },
    ];
  }

  // -------------------------------------------------------------------
  // Model change handlers — merge partial updates back into the shared model
  // -------------------------------------------------------------------

  onMainModelChange(partial: Partial<PrintConfig>): void {
    const prev = this._appliedModel.sheetFormatId;
    const prevCodeType = this._appliedModel.codeType;
    const prevLayoutId = this._appliedModel.layoutId;
    this.printConfigModel = { ...this.printConfigModel, ...partial };
    this._appliedModel = { ...this.printConfigModel };

    if (partial.layoutId !== undefined && partial.layoutId !== prevLayoutId) {
      this.userStorage.setItem('label-printing:layout-id', partial.layoutId ?? '');
    }

    if (partial.codeType !== undefined && partial.codeType !== prevCodeType) {
      this.mainFields = this.buildMainFields();
    }

    if (partial.sheetFormatId !== undefined && partial.sheetFormatId !== prev) {
      this.userStorage.setItem('label-printing:format-id', partial.sheetFormatId ?? '');
      if (partial.sheetFormatId) {
        const fmt = this.sheetFormats().find((f) => f.id === partial.sheetFormatId);
        const totalCells = fmt ? fmt.columns * fmt.rows : 0;
        const restored = this.loadCellState(partial.sheetFormatId, totalCells);
        this.labeledPositions.set(restored.labeled);
        this.usedPositions.set(restored.used);

        const layout = this.labelLayouts().find((l) => l.id === this.printConfigModel.layoutId);
        const savedSettings = layout && fmt ? this.loadLayoutSettings(layout, fmt) : null;
        if (savedSettings) {
          this.printConfigModel = {
            ...this.printConfigModel,
            codeType: savedSettings.codeType,
            fontFamily: savedSettings.fontFamily,
            showCode: savedSettings.showCode,
            showCheckpointName: savedSettings.showCheckpointName,
            showFormName: savedSettings.showFormName,
            showSerialNumber: savedSettings.showSerialNumber,
            checkpointNameFontSize: savedSettings.checkpointNameFontSize,
            formNameFontSize: savedSettings.formNameFontSize,
            serialNumberFontSize: savedSettings.serialNumberFontSize,
            qrX: savedSettings.qrX,
            qrY: savedSettings.qrY,
            qrWidth: savedSettings.qrWidth,
            qrHeight: savedSettings.qrHeight,
            checkpointNameX: savedSettings.checkpointNameX,
            checkpointNameY: savedSettings.checkpointNameY,
            checkpointNameWidth: savedSettings.checkpointNameWidth,
            checkpointNameHeight: savedSettings.checkpointNameHeight,
            formNameX: savedSettings.formNameX,
            formNameY: savedSettings.formNameY,
            formNameWidth: savedSettings.formNameWidth,
            formNameHeight: savedSettings.formNameHeight,
            serialNumberX: savedSettings.serialNumberX,
            serialNumberY: savedSettings.serialNumberY,
            serialNumberWidth: savedSettings.serialNumberWidth,
            serialNumberHeight: savedSettings.serialNumberHeight,
          };
        }

        const checkpointId = this.checkPoint()?.id;
        if (this.hasInstances() && checkpointId) {
          this.loadInstanceAssignments(checkpointId, partial.sheetFormatId, this.instances());
          this.ensureSelectedInstance();
        }

        // Rebuild fields so the sheet select options + restored values are current
        this.mainFields = this.buildMainFields();
      }
    } else {
      // Non-sheet changes are layout settings — persist them
      this.saveLayoutSettings();
    }
  }

  onPreviewBoundsChange(patch: LabelPreviewPatch): void {
    this.printConfigModel = { ...this.printConfigModel, ...patch };
    this._appliedModel = { ...this.printConfigModel };
    this.saveLayoutSettings();
  }

  async onCreateSheetFormat(): Promise<void> {
    const base = this.selectedSheetFormat ?? this.sheetFormats()[0] ?? null;
    if (!base) return;

    const unit = this.loadSheetEditorUnit();
    const draft = this.toEditorModel(base, unit);
    draft.name = `${base.name} ${this.translate.translate('labelPrinting.sheetFormatEditor.copySuffix')}`;
    await this.openSheetFormatEditor('create', draft);
  }

  async onEditSelectedSheetFormat(): Promise<void> {
    const format = this.selectedSheetFormat;
    if (!format || format.is_system === 1) return;
    await this.openSheetFormatEditor(
      'edit',
      this.toEditorModel(format, this.loadSheetEditorUnit()),
    );
  }

  async onDeleteSelectedSheetFormat(): Promise<void> {
    const format = this.selectedSheetFormat;
    if (!format || format.is_system === 1) return;

    const closeSignal = signal<{ action: string; data?: any } | null>(null);
    const config: ModalConfig = {
      component: SheetFormatDeleteConfirmModalComponent,
      componentInputs: {
        formatName: format.name,
      },
      title: this.translate.translate('labelPrinting.sheetFormatDelete.title'),
      size: 'small',
      closeSignal,
      footerClassName: 'flex flex-wrap gap-md flex-responsive-xs col-2',
      actionButtons: [
        {
          id: 'cancel',
          label: this.translate.translate('button.cancel'),
          variant: 'secondary',
          onClick: async () => closeSignal.set({ action: 'cancel' }),
        },
        {
          id: 'delete',
          label: this.translate.translate('button.delete'),
          className: 'ml-auto',
          variant: 'danger',
          onClick: async () => closeSignal.set({ action: 'delete' }),
        },
      ],
    };

    const result = await this.modalService.openModal(config);
    if (result?.action !== 'delete') return;

    try {
      await this.sheetFormatService.delete(format.id);
      await this.refreshSheetFormats();
      this.toast.success(this.translate.translate('labelPrinting.sheetFormatDelete.success'));
    } catch (error) {
      this.toast.error(this.getErrorMessage(error));
    }
  }

  private async openSheetFormatEditor(
    mode: 'create' | 'edit',
    initialDraft: SheetFormatEditorModel,
  ): Promise<void> {
    const draftSignal = signal<SheetFormatEditorModel>({ ...initialDraft });
    const closeSignal = signal<{ action: string; data?: any } | null>(null);
    const saveDisabled = signal<string | null>(null);

    const config: ModalConfig = {
      component: SheetFormatEditorModalComponent,
      componentInputs: {
        modelSignal: draftSignal,
        saveDisabledSignal: saveDisabled,
      },
      title: this.translate.translate(
        mode === 'create'
          ? 'labelPrinting.sheetFormatEditor.titleCreate'
          : 'labelPrinting.sheetFormatEditor.titleEdit',
      ),
      size: 'large',
      closeSignal,
      footerClassName: 'flex flex-wrap gap-md flex-responsive-xs col-2',
      actionButtons: [
        {
          id: 'cancel',
          label: this.translate.translate('button.cancel'),
          variant: 'secondary',
          onClick: async () => closeSignal.set({ action: 'cancel' }),
        },
        {
          id: 'save',
          label: this.translate.translate('button.save'),
          className: 'ml-auto',
          variant: 'primary',
          disabled: saveDisabled as any,
          onClick: async () => closeSignal.set({ action: 'save' }),
        },
      ],
    };

    const result = await this.modalService.openModal(config);
    if (result?.action !== 'save') return;

    const draft = draftSignal();
    this.userStorage.setItem(LabelPrintingComponent.SHEET_EDITOR_UNIT_STORAGE_KEY, draft.unit);

    try {
      const values = this.toUpsertValues(draft);
      if (mode === 'create') {
        const created = await this.sheetFormatService.create(values);
        await this.refreshSheetFormats(created.id);
        this.toast.success(this.translate.translate('labelPrinting.sheetFormatEditor.created'));
      } else {
        const selected = this.selectedSheetFormat;
        if (!selected) return;
        const updated = await this.sheetFormatService.update({
          id: selected.id,
          values,
        });
        await this.refreshSheetFormats(updated.id);
        this.toast.success(this.translate.translate('labelPrinting.sheetFormatEditor.updated'));
      }
    } catch (error) {
      this.toast.error(this.getErrorMessage(error));
    }
  }

  private async refreshSheetFormats(preferredId?: string): Promise<void> {
    const formats = await this.sheetFormatService.getAll();
    this.sheetFormats.set(formats);

    const current = this.printConfigModel.sheetFormatId;
    const target =
      preferredId ??
      (current && formats.some((f) => f.id === current) ? current : (formats[0]?.id ?? null));

    this.onMainModelChange({ sheetFormatId: target });
  }

  private loadSheetEditorUnit(): SheetFormatEditorUnit {
    const stored = this.userStorage.getItem(LabelPrintingComponent.SHEET_EDITOR_UNIT_STORAGE_KEY);
    return stored === 'mm' ? 'mm' : 'in';
  }

  private toEditorModel(format: SheetFormat, unit: SheetFormatEditorUnit): SheetFormatEditorModel {
    return {
      unit,
      name: format.name,
      product_code: format.product_code,
      manufacturer: format.manufacturer,
      page_width: this.convertMmToUnit(format.page_width, unit),
      page_height: this.convertMmToUnit(format.page_height, unit),
      label_width: this.convertMmToUnit(format.label_width, unit),
      label_height: this.convertMmToUnit(format.label_height, unit),
      columns: format.columns,
      rows: format.rows,
      margin_top: this.convertMmToUnit(format.margin_top, unit),
      margin_left: this.convertMmToUnit(format.margin_left, unit),
      gutter_horizontal: this.convertMmToUnit(format.gutter_horizontal, unit),
      gutter_vertical: this.convertMmToUnit(format.gutter_vertical, unit),
      corner_radius: this.convertMmToUnit(format.corner_radius, unit),
    };
  }

  private toUpsertValues(model: SheetFormatEditorModel): SheetFormatUpsertValues {
    return {
      name: model.name,
      product_code: model.product_code,
      manufacturer: model.manufacturer,
      page_width: this.convertUnitToMm(this.asNumber(model.page_width), model.unit),
      page_height: this.convertUnitToMm(this.asNumber(model.page_height), model.unit),
      label_width: this.convertUnitToMm(this.asNumber(model.label_width), model.unit),
      label_height: this.convertUnitToMm(this.asNumber(model.label_height), model.unit),
      columns: Math.round(this.asNumber(model.columns)),
      rows: Math.round(this.asNumber(model.rows)),
      margin_top: this.convertUnitToMm(this.asNumber(model.margin_top), model.unit),
      margin_left: this.convertUnitToMm(this.asNumber(model.margin_left), model.unit),
      gutter_horizontal: this.convertUnitToMm(this.asNumber(model.gutter_horizontal), model.unit),
      gutter_vertical: this.convertUnitToMm(this.asNumber(model.gutter_vertical), model.unit),
      corner_radius: this.convertUnitToMm(this.asNumber(model.corner_radius), model.unit),
    };
  }

  private convertMmToUnit(value: number, unit: SheetFormatEditorUnit): number {
    if (unit === 'mm') return Number(value.toFixed(2));
    return Number((value / LabelPrintingComponent.MM_PER_INCH).toFixed(4));
  }

  private convertUnitToMm(value: number, unit: SheetFormatEditorUnit): number {
    if (unit === 'mm') return Number(value.toFixed(2));
    return Number((value * LabelPrintingComponent.MM_PER_INCH).toFixed(4));
  }

  private asNumber(value: unknown): number {
    if (typeof value === 'number') return value;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private getErrorMessage(error: unknown): string {
    if (error instanceof Error && error.message) {
      return error.message;
    }
    return this.translate.translate('labelPrinting.error.loadFailed');
  }

  // -------------------------------------------------------------------
  // Storage helpers
  // -------------------------------------------------------------------

  private cellStateStorageKey(type: 'labeled' | 'used', formatId: string): string {
    return `label-printing:${type}-positions:${formatId}`;
  }

  private saveCellState(): void {
    const formatId = this.printConfigModel.sheetFormatId;
    if (!formatId) return;
    this.userStorage.setTypedItem<number[]>(
      this.cellStateStorageKey('labeled', formatId),
      Array.from(this.labeledPositions()),
    );
    this.userStorage.setTypedItem<number[]>(
      this.cellStateStorageKey('used', formatId),
      Array.from(this.usedPositions()),
    );
  }

  private loadCellState(
    formatId: string,
    totalCells: number,
  ): { labeled: Set<number>; used: Set<number> } {
    const parse = (key: string): Set<number> => {
      const raw = this.userStorage.getTypedItem<number[]>(key);
      if (!Array.isArray(raw)) return new Set();
      return new Set(raw.filter((n) => typeof n === 'number' && n >= 0 && n < totalCells));
    };
    return {
      labeled: parse(this.cellStateStorageKey('labeled', formatId)),
      used: parse(this.cellStateStorageKey('used', formatId)),
    };
  }

  private layoutSettingsStorageKey(formatId: string): string {
    return `label-printing:layout-settings:${formatId}`;
  }

  private getDefaultPositionSettings(
    layout: LabelLayout,
    format: SheetFormat,
  ): Required<LayoutSettings> {
    const pad = layout.layoutConfig.padding;
    const usableW = format.label_width - pad.left - pad.right;
    const usableH = format.label_height - pad.top - pad.bottom;
    const qr = layout.layoutConfig.qr;
    const cpRegion = layout.layoutConfig.textRegions.find((r) => r.field === 'checkpointName');
    const fnRegion = layout.layoutConfig.textRegions.find((r) => r.field === 'formName');
    const snRegion = layout.layoutConfig.textRegions.find((r) => r.field === 'serialNumber');
    return {
      codeType: 'qr',
      fontFamily: 'helvetica',
      showCode: true,
      showCheckpointName: true,
      showFormName: true,
      showSerialNumber: true,
      checkpointNameFontSize: 10,
      formNameFontSize: 8,
      serialNumberFontSize: 8,
      qrX: pad.left + qr.x * usableW,
      qrY: pad.top + qr.y * usableH,
      qrWidth: qr.width * usableW,
      qrHeight: qr.height * usableH,
      checkpointNameX: cpRegion ? pad.left + cpRegion.x * usableW : 0,
      checkpointNameY: cpRegion ? pad.top + cpRegion.y * usableH : 0,
      checkpointNameWidth: cpRegion ? cpRegion.width * usableW : usableW,
      checkpointNameHeight: cpRegion ? cpRegion.height * usableH : usableH * 0.25,
      formNameX: fnRegion ? pad.left + fnRegion.x * usableW : 0,
      formNameY: fnRegion ? pad.top + fnRegion.y * usableH : 0,
      formNameWidth: fnRegion ? fnRegion.width * usableW : usableW,
      formNameHeight: fnRegion ? fnRegion.height * usableH : usableH * 0.25,
      serialNumberX: snRegion ? pad.left + snRegion.x * usableW : 0,
      serialNumberY: snRegion ? pad.top + snRegion.y * usableH : 0,
      serialNumberWidth: snRegion ? snRegion.width * usableW : usableW,
      serialNumberHeight: snRegion ? snRegion.height * usableH : usableH * 0.25,
    };
  }

  private loadLayoutSettings(layout: LabelLayout, format: SheetFormat): Required<LayoutSettings> {
    const defaults = this.getDefaultPositionSettings(layout, format);
    const raw = this.userStorage.getItem(this.layoutSettingsStorageKey(format.id));
    if (!raw) return defaults;
    try {
      const saved = JSON.parse(raw) as LayoutSettings;
      return { ...defaults, ...saved };
    } catch {
      return defaults;
    }
  }

  private saveLayoutSettings(): void {
    const format = this.selectedSheetFormat;
    if (!format) return;
    const settings: LayoutSettings = {
      codeType: this.printConfigModel.codeType,
      fontFamily: this.printConfigModel.fontFamily,
      showCode: this.printConfigModel.showCode,
      showCheckpointName: this.printConfigModel.showCheckpointName,
      showFormName: this.printConfigModel.showFormName,
      showSerialNumber: this.printConfigModel.showSerialNumber,
      checkpointNameFontSize: this.printConfigModel.checkpointNameFontSize,
      formNameFontSize: this.printConfigModel.formNameFontSize,
      serialNumberFontSize: this.printConfigModel.serialNumberFontSize,
      qrX: this.printConfigModel.qrX,
      qrY: this.printConfigModel.qrY,
      qrWidth: this.printConfigModel.qrWidth,
      qrHeight: this.printConfigModel.qrHeight,
      checkpointNameX: this.printConfigModel.checkpointNameX,
      checkpointNameY: this.printConfigModel.checkpointNameY,
      checkpointNameWidth: this.printConfigModel.checkpointNameWidth,
      checkpointNameHeight: this.printConfigModel.checkpointNameHeight,
      formNameX: this.printConfigModel.formNameX,
      formNameY: this.printConfigModel.formNameY,
      formNameWidth: this.printConfigModel.formNameWidth,
      formNameHeight: this.printConfigModel.formNameHeight,
      serialNumberX: this.printConfigModel.serialNumberX,
      serialNumberY: this.printConfigModel.serialNumberY,
      serialNumberWidth: this.printConfigModel.serialNumberWidth,
      serialNumberHeight: this.printConfigModel.serialNumberHeight,
    };
    this.userStorage.setItem(this.layoutSettingsStorageKey(format.id), JSON.stringify(settings));
  }

  /** Cycles a cell through: blank → labeled → used → blank */
  onPositionToggled(position: number): void {
    // For instanced checkpoints, use instance-aware position handling
    if (this.hasInstances()) {
      this.onPositionClickForInstance(position);
      return;
    }

    const labeled = new Set(this.labeledPositions());
    const used = new Set(this.usedPositions());
    if (labeled.has(position)) {
      // labeled → used
      labeled.delete(position);
      used.add(position);
    } else if (used.has(position)) {
      // used → blank
      used.delete(position);
    } else {
      // blank → labeled
      labeled.add(position);
    }
    this.labeledPositions.set(labeled);
    this.usedPositions.set(used);
    this.saveCellState();
  }

  onPositionRightClicked(position: number): void {
    const labeled = new Set(this.labeledPositions());
    const used = new Set(this.usedPositions());
    labeled.delete(position);
    used.add(position);
    this.labeledPositions.set(labeled);
    this.usedPositions.set(used);
    this.saveCellState();
  }

  onSelectAll(): void {
    const fmt = this.selectedSheetFormat;
    if (!fmt) return;
    const all = new Set<number>();
    for (let i = 0; i < fmt.columns * fmt.rows; i++) {
      all.add(i);
    }
    // Select All always fills labeled cells; clear overlap from used
    const used = new Set(this.usedPositions());
    for (const p of all) used.delete(p);
    this.labeledPositions.set(all);
    this.usedPositions.set(used);
    this.saveCellState();
  }

  onClearAll(): void {
    this.labeledPositions.set(new Set());
    this.usedPositions.set(new Set());

    // In instance mode, clear persisted assignment state too so legacy/stale
    // labeled cells from older storage formats are fully reset.
    if (this.hasInstances()) {
      this.instanceAssignments.set(new Map());
      this.ensureSelectedInstance();
      this.saveInstanceAssignments();
    }

    this.saveCellState();
  }

  /** Converts all currently labeled cells to used (call after peeling + applying labels). */
  onMarkLabeledAsUsed(): void {
    const combined = new Set([...this.usedPositions(), ...this.labeledPositions()]);
    this.labeledPositions.set(new Set());
    this.usedPositions.set(combined);
    this.saveCellState();
  }

  async onPrint(): Promise<void> {
    const layout = this.selectedLayout;
    const format = this.selectedSheetFormat;
    const cp = this.checkPoint();
    const form = this.form();

    if (!layout || !format || !cp || !form) return;

    this.generating.set(true);
    try {
      const isBarcode = this.printConfigModel.codeType === 'barcode';
      let barcodeValues: Map<number, string> | undefined;
      let sharedBarcodeValue: string | undefined;

      if (isBarcode) {
        barcodeValues = new Map<number, string>();
        const hasInstances = this.hasInstances() && this.instanceAssignments().size > 0;

        if (hasInstances) {
          // For instanced labels, each position gets its own barcode mapping
          for (const [position, instance] of this.instanceAssignments()) {
            if (this.labeledPositions().has(position)) {
              const mapping = await this.barcodeMappingService.getOrCreateForTarget(
                cp.id!,
                form.id!,
                instance.id!,
              );
              barcodeValues.set(position, mapping.barcode_value);
            }
          }
        } else {
          // Shared barcode for all positions
          const mapping = await this.barcodeMappingService.getOrCreateForTarget(cp.id!, form.id!);
          sharedBarcodeValue = mapping.barcode_value;
          for (const position of this.labeledPositions()) {
            barcodeValues.set(position, mapping.barcode_value);
          }
        }
      }

      const doc = await this.labelPdfService.generatePdf({
        sheetFormat: format,
        layout,
        selectedPositions: this.labeledPositions(),
        checkpointName: cp.name,
        formName: form.name,
        qrUrl: this.qrUrl,
        codeType: this.printConfigModel.codeType,
        barcodeValues,
        sharedBarcodeValue,
        showCheckpointName: this.printConfigModel.showCheckpointName,
        showFormName: this.printConfigModel.showFormName,
        showSerialNumber: this.printConfigModel.showSerialNumber,
        showCode: this.printConfigModel.showCode,
        checkpointNameFontSize: this.printConfigModel.checkpointNameFontSize,
        formNameFontSize: this.printConfigModel.formNameFontSize,
        serialNumberFontSize: this.printConfigModel.serialNumberFontSize,
        fontFamily: this.printConfigModel.fontFamily,
        qrX: this.printConfigModel.qrX,
        qrY: this.printConfigModel.qrY,
        qrWidth: this.printConfigModel.qrWidth,
        qrHeight: this.printConfigModel.qrHeight,
        checkpointNameX: this.printConfigModel.checkpointNameX,
        checkpointNameY: this.printConfigModel.checkpointNameY,
        checkpointNameWidth: this.printConfigModel.checkpointNameWidth,
        checkpointNameHeight: this.printConfigModel.checkpointNameHeight,
        formNameX: this.printConfigModel.formNameX,
        formNameY: this.printConfigModel.formNameY,
        formNameWidth: this.printConfigModel.formNameWidth,
        formNameHeight: this.printConfigModel.formNameHeight,
        serialNumberX: this.printConfigModel.serialNumberX,
        serialNumberY: this.printConfigModel.serialNumberY,
        serialNumberWidth: this.printConfigModel.serialNumberWidth,
        serialNumberHeight: this.printConfigModel.serialNumberHeight,
        debug: this.debug(),
        instanceAssignments: this.hasInstances() ? this.instanceAssignments() : undefined,
      });

      this.labelPdfService.printPdf(doc);
    } catch (err) {
      this.logger.error('Failed to generate label PDF:', err);
      this.error.set(this.translate.translate('labelPrinting.error.generateFailed'));
    } finally {
      this.generating.set(false);
    }
  }

  goBack(): void {
    const cp = this.checkPoint();
    const form = this.form();
    if (cp && form) {
      this.router.navigate(['/checkpoints', cp.id, form.id, 'view']);
    } else {
      this.router.navigate(['/checkpoints']);
    }
  }

  // -------------------------------------------------------------------
  // Instance assignment methods (for instanced checkpoints)
  // -------------------------------------------------------------------

  /** Select an instance from the sidebar for placement */
  selectInstance(instance: AssetInstance): void {
    this.selectedInstance.set(instance);
    this.saveSelectedInstanceId(instance.id);
  }

  /** Place the selected instance on a grid position, or cycle labeled → used → blank */
  onPositionClickForInstance(position: number): void {
    const assignments = new Map(this.instanceAssignments());
    const labeled = new Set(this.labeledPositions());
    const used = new Set(this.usedPositions());

    if (assignments.has(position)) {
      if (labeled.has(position)) {
        // labeled → used (keep assignment for reference)
        labeled.delete(position);
        used.add(position);
      } else if (used.has(position)) {
        // used → blank (remove assignment)
        used.delete(position);
        assignments.delete(position);
      }
      this.instanceAssignments.set(assignments);
      this.labeledPositions.set(labeled);
      this.usedPositions.set(used);
      this.saveCellState();
      this.saveInstanceAssignments();
      return;
    }

    // Legacy migration path: allow clearing stale labeled/used cells that may
    // exist in userStorage without a corresponding instance assignment.
    if (labeled.has(position) || used.has(position)) {
      labeled.delete(position);
      used.delete(position);
      this.labeledPositions.set(labeled);
      this.usedPositions.set(used);
      this.saveCellState();
      return;
    }

    const selected = this.selectedInstance();
    if (!selected) return;

    // Place the selected instance
    assignments.set(position, selected);
    labeled.add(position);
    this.instanceAssignments.set(assignments);
    this.labeledPositions.set(labeled);
    this.saveCellState();
    this.saveInstanceAssignments();
  }

  /** Remove a specific instance assignment */
  removeInstanceAssignment(position: number): void {
    const assignments = new Map(this.instanceAssignments());
    const labeled = new Set(this.labeledPositions());
    assignments.delete(position);
    labeled.delete(position);
    this.instanceAssignments.set(assignments);
    this.labeledPositions.set(labeled);
    this.saveCellState();
    this.saveInstanceAssignments();
  }

  /** Auto-fill blank cells with unassigned instances in order; stops at the last serial number. */
  /** Auto-fill the next empty cells with one full set of instances (in order), then stop. */
  autoFillInstances(): void {
    const fmt = this.selectedSheetFormat;
    if (!fmt) return;

    const assignments = new Map(this.instanceAssignments());
    const labeled = new Set(this.labeledPositions());
    const used = this.usedPositions();
    const assignable = this.assignableInstances;
    if (assignable.length === 0) return;
    const totalCells = fmt.columns * fmt.rows;

    // Fill empty cells in position order with assignable[0..N-1]; stop after one full set.
    // Each click places the next set of N instances into the next N empty slots.
    let instanceIdx = 0;
    for (let pos = 0; pos < totalCells; pos++) {
      if (instanceIdx >= assignable.length) break;
      if (!assignments.has(pos) && !used.has(pos) && !labeled.has(pos)) {
        assignments.set(pos, assignable[instanceIdx]);
        labeled.add(pos);
        instanceIdx++;
      }
    }

    this.instanceAssignments.set(assignments);
    this.labeledPositions.set(labeled);
    this.saveCellState();
    this.saveInstanceAssignments();
  }

  /** Clear all instance assignments */
  clearInstanceAssignments(): void {
    // Reset full sheet state for instance mode (assignments + cell states).
    this.onClearAll();
  }

  /** Get the serial number assigned to a position (for grid display) */
  getSerialForPosition(position: number): string {
    return this.instanceAssignments().get(position)?.serial_number ?? '';
  }

  // -------------------------------------------------------------------
  // Instance assignment persistence
  // -------------------------------------------------------------------

  private instanceAssignmentStorageKey(checkpointId: string, formatId: string): string {
    return `label-printing:instance-assignments:${checkpointId}:${formatId}`;
  }

  private selectedInstanceStorageKey(checkpointId: string): string {
    return `${LabelPrintingComponent.SELECTED_INSTANCE_STORAGE_KEY_PREFIX}:${checkpointId}`;
  }

  private saveSelectedInstanceId(instanceId: string): void {
    const checkpointId = this.checkPoint()?.id;
    if (!checkpointId) return;
    this.userStorage.setItem(this.selectedInstanceStorageKey(checkpointId), instanceId);
  }

  private ensureSelectedInstance(): void {
    const checkpointId = this.checkPoint()?.id;
    const assignable = this.assignableInstances;

    if (!checkpointId || assignable.length === 0) {
      this.selectedInstance.set(null);
      if (checkpointId) {
        this.userStorage.removeItem(this.selectedInstanceStorageKey(checkpointId));
      }
      return;
    }

    const current = this.selectedInstance();
    if (current && assignable.some((instance) => instance.id === current.id)) {
      return;
    }

    const savedInstanceId = this.userStorage.getItem(this.selectedInstanceStorageKey(checkpointId));
    const restored = savedInstanceId
      ? (assignable.find((instance) => instance.id === savedInstanceId) ?? assignable[0])
      : assignable[0];

    this.selectedInstance.set(restored);
    this.saveSelectedInstanceId(restored.id);
  }

  private saveInstanceAssignments(): void {
    const cp = this.checkPoint();
    const formatId = this.printConfigModel.sheetFormatId;
    if (!cp?.id || !formatId) return;

    const map: Record<string, string> = {};
    for (const [pos, instance] of this.instanceAssignments()) {
      map[String(pos)] = instance.id;
    }
    this.userStorage.setTypedItem(this.instanceAssignmentStorageKey(cp.id, formatId), map);
  }

  private loadInstanceAssignments(
    checkpointId: string,
    formatId: string,
    instances: AssetInstance[],
  ): void {
    const raw = this.userStorage.getTypedItem<Record<string, string>>(
      this.instanceAssignmentStorageKey(checkpointId, formatId),
    );
    if (!raw || typeof raw !== 'object') return;

    const instanceMap = new Map(instances.map((i) => [i.id, i]));
    const assignments = new Map<number, AssetInstance>();
    const labeled = new Set(this.labeledPositions());

    for (const [posStr, instanceId] of Object.entries(raw)) {
      const pos = Number(posStr);
      const instance = instanceMap.get(instanceId);
      if (!Number.isNaN(pos) && instance) {
        assignments.set(pos, instance);
        labeled.add(pos);
      }
    }

    this.instanceAssignments.set(assignments);
    this.labeledPositions.set(labeled);
  }
}
